import { MyreApp } from '@myre/common-core';
import { DWH_END_DATE, DWH_START_DATE } from '@myre/common-dwh';
import { defaultLangLocale, LangLocale, languages } from '@myre/common-i18n';
import { Scope } from '@myre/domain-i18n-core';
import { DynamicEnvService } from '@myre/ui-domain-http';
import {
  APP_BASE_HREF_PROVIDER,
  EVENT_MANAGER_PROVIDER,
  LANGLOCALE,
  MYRE_I18N_SCOPE,
  PROTOCOL_PROVIDER,
  WINDOW_PROVIDER,
} from '@myre/ui-primitive-core';
import {
  FORMS_CONFIG,
  GOOGLE_MAP_API_KEY,
  type FormsConfig,
} from '@myre/ui-primitive-forms';

import { registerLocaleData } from '@angular/common';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import {
  APP_INITIALIZER,
  ErrorHandler,
  LOCALE_ID,
  makeEnvironmentProviders,
  Type,
  type EnvironmentProviders,
  type Provider,
} from '@angular/core';
import { DefaultUrlSerializer, UrlSerializer, UrlTree } from '@angular/router';
import { CookieService } from 'ngx-cookie-service';

import { getLangCookie, setLangCookie } from './cookies/lang-cookie.helpers';
import { angularLocales } from './helpers/locales.helpers';
import { ApiErrorInterceptor } from './interceptors/api-error.interceptor';
import {
  CredentialsInterceptor,
  MfaInterceptor,
} from './interceptors/credentials.interceptor';
import { MyreWordingsLoader, WORDINGS } from './myre-wordings-loader';
import { providePrimitiveWordings } from './primitive-wordings.providers';
import {
  BaseAuthService,
  MYRE_APP,
  type BaseContextIds,
} from './services/auth/auth.service';
import {
  CONTEXT_IDS,
  RouterStateSerializer,
  STRINGS_PARAM_KEYS,
} from './services/auth/dispatch.guard';
import { CustomErrorHandler } from './services/errors/error-handler.service';

for (const angularLocale of Object.values(angularLocales)) {
  registerLocaleData(angularLocale);
}

/**
 * Every date a form accepts is one the warehouse can hold: a question that
 * needs a narrower range declares its own bounds.
 */
function formsProviderFactory(): FormsConfig {
  return {
    minDate: DWH_START_DATE,
    maxDate: DWH_END_DATE,
    minNumber: Number.MIN_SAFE_INTEGER,
    maxNumber: Number.MAX_SAFE_INTEGER,
  };
}

function langLocaleProviderFactory(
  cookieService: CookieService,
  dynamicEnvService: DynamicEnvService,
): string {
  const { cookieSuffix, domain } = dynamicEnvService.env;
  const lang = getLangCookie({ cookieService, cookieSuffix });

  if (lang) {
    return lang;
  }

  setLangCookie({
    cookieService,
    cookieSuffix,
    domain,
    lang: defaultLangLocale,
  });

  return defaultLangLocale;
}

function localeIdProviderFactory(lang: LangLocale): string {
  return languages[lang].angularLocale;
}

export interface MyreModuleConfig {
  app?: MyreApp;
  authService?: Type<BaseAuthService>;
  i18nScope?: Scope | Scope[];
  routerStateSerializer?: {
    class: Type<RouterStateSerializer>;
    contextIds: BaseContextIds;
    stringsParamKeys?: string[];
  };
}

class CustomUrlSerializer extends DefaultUrlSerializer {
  /**
   * Removes parentheses from primary child routes of property
   */
  public override serialize(tree: UrlTree): string {
    return super.serialize(tree).replace(/\(([\w-/]+)\)/g, '$1');
  }
}

function googleMapApiKeyProviderFactory(
  dynamicEnvService: DynamicEnvService,
): string {
  return dynamicEnvService.env.googleMap?.key;
}

function initFactory(
  wordingsLoader: MyreWordingsLoader,
  lang: LangLocale,
): () => Promise<void> {
  return () => wordingsLoader.load(lang);
}

export function provideMyre(config?: MyreModuleConfig): EnvironmentProviders {
  const providers: Array<Provider | EnvironmentProviders> = [
    MyreWordingsLoader,
    {
      provide: APP_INITIALIZER,
      useFactory: initFactory,
      deps: [MyreWordingsLoader, LANGLOCALE],
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useExisting: CredentialsInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useExisting: MfaInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useExisting: ApiErrorInterceptor,
      multi: true,
    },
    {
      provide: ErrorHandler,
      useClass: CustomErrorHandler,
    },
    {
      provide: GOOGLE_MAP_API_KEY,
      useFactory: googleMapApiKeyProviderFactory,
      deps: [DynamicEnvService],
    },
    EVENT_MANAGER_PROVIDER,
    WINDOW_PROVIDER,
    {
      provide: LANGLOCALE,
      useFactory: langLocaleProviderFactory,
      deps: [CookieService, DynamicEnvService],
    },
    {
      provide: LOCALE_ID,
      useFactory: localeIdProviderFactory,
      deps: [LANGLOCALE],
    },
    APP_BASE_HREF_PROVIDER,
    {
      provide: FORMS_CONFIG,
      useFactory: formsProviderFactory,
    },
    {
      provide: WORDINGS,
      useFactory: (wordingsLoader: MyreWordingsLoader) =>
        wordingsLoader.wordings,
      deps: [MyreWordingsLoader],
    },
    ...providePrimitiveWordings(),
    PROTOCOL_PROVIDER,
    { provide: UrlSerializer, useClass: CustomUrlSerializer },
  ];

  if (config?.app) {
    providers.push({
      provide: MYRE_APP,
      useValue: config.app,
    });
  }

  if (config?.i18nScope) {
    providers.push({
      provide: MYRE_I18N_SCOPE,
      useValue: config.i18nScope,
    });
  }

  if (config?.authService) {
    providers.push({
      provide: BaseAuthService,
      useExisting: config.authService,
    });
  }

  if (config?.routerStateSerializer) {
    if (config.routerStateSerializer.stringsParamKeys) {
      providers.push({
        provide: STRINGS_PARAM_KEYS,
        useValue: config.routerStateSerializer.stringsParamKeys,
      });
    }

    providers.push(
      {
        provide: CONTEXT_IDS,
        useValue: config.routerStateSerializer.contextIds,
      },
      config.routerStateSerializer.class,
      {
        provide: RouterStateSerializer,
        useExisting: config.routerStateSerializer.class,
      },
    );
  }

  return makeEnvironmentProviders(providers);
}
